/**
 * v4.15.0 — Inline conflict banner.
 *
 * Rendered directly beneath the block it concerns. The operator sees
 * the problem AND the fix in the same eyeline — no toast, no
 * hunting, no memorization.
 */
import { AlertTriangle, Wand2 } from 'lucide-react';
import { fmtDuration, dayLabel } from './scheduleAnalyzer';

export default function ConflictBanner({ conflicts, onApplyFix }) {
    if (!conflicts || conflicts.length === 0) return null;
    // Show the most actionable conflict first; collapse the rest into a count.
    const first = conflicts[0];
    const remaining = conflicts.length - 1;

    return (
        <div
            className="rounded-md border-2 border-red-400/70 bg-red-50/80 dark:bg-red-950/30 px-2.5 py-2 text-xs space-y-1.5"
            data-testid="conflict-banner"
        >
            <div className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="text-red-700 dark:text-red-200 leading-snug">
                    <strong>Overlaps Block #{first.other_index}</strong> on {dayLabel(first.day)}
                    {' '}({fmtDuration(first.overlap_min)}): {first.a.startStr}–{first.a.endStr} vs {first.b.startStr}–{first.b.endStr}.
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 ml-5">
                {first.fixes.slice(0, 2).map((fix, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => onApplyFix(fix)}
                        data-testid={`conflict-fix-${fix.kind}-${i}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border border-red-400/60 bg-white dark:bg-slate-900 text-red-700 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                    >
                        <Wand2 className="w-3 h-3" />
                        {fix.label}
                    </button>
                ))}
            </div>

            {remaining > 0 && (
                <div className="ml-5 text-[10px] text-red-600/80 dark:text-red-300/80">
                    + {remaining} more conflict{remaining === 1 ? '' : 's'} on this block — fixing the first one will usually resolve the rest.
                </div>
            )}
        </div>
    );
}
